import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { customsV2Service } from '@/services'
import { ApiResponse } from '@/services/api'
import { HouseBillSchema, MasterBillSchema } from '@/services/CustomsV2Service'

interface MasterBillState {
  loading: boolean
  error: string | null
  success: string | null
  masterBill: MasterBillSchema | null
  houseBills: HouseBillSchema[]
  houseBill: HouseBillSchema | null
  totalMarks: number
  printDialog: boolean
  removeDialog: boolean
  noteDialog: boolean
}

interface MasterBillActions {
  // 获取MBL数据
  fetchMasterBill: (masterBillId: number) => Promise<ApiResponse>
  // 更新MBL数据
  updateMasterBill: (data: MasterBillSchema) => void
  // 更新MBL的HBL其中一个
  updateFindHouseInMaster: (data: HouseBillSchema) => Promise<void>
  // 清除MBL数据
  clearMasterBill: () => void
  // 更新HBL的备注
  updateNoteByHouseBill: (note: string) => Promise<ApiResponse>
  // 删除MBL
  removeMasterBill: (masterBillId: number) => Promise<ApiResponse>

  setHouseBill: (houseBillId: number) => void

  // 计算mark总数
  calculateTotalMarks: () => void

  // 打印事件
  showPrintDialog: () => void
  closePrintDialog: () => void
  // 删除事件
  showRemoveDialog: () => void
  closeRemoveDialog: () => void
  // 更新备注事件
  showNoteDialog: () => void
  closeNoteDialog: () => void
}

type MasterBillStore = MasterBillState & MasterBillActions

const initialState: MasterBillState = {
  loading: false,
  error: null,
  success: null,
  masterBill: null,
  houseBill: null,
  houseBills: [],
  totalMarks: 0,
  printDialog: false,
  removeDialog: false,
  noteDialog: false
}

export const useMasterBillStore = create<MasterBillStore>()(
  devtools(
    
      (set, get) => ({
        ...initialState,
        showPrintDialog: () => set({ printDialog: true }),
        closePrintDialog: () => set({ printDialog: false }),
        showRemoveDialog: () => set({ removeDialog: true }),
        closeRemoveDialog: () => set({ removeDialog: false }),
        showNoteDialog: () => set({ noteDialog: true }),
        closeNoteDialog: () => set({ noteDialog: false }),
        clearMasterBill: () => set(initialState),

        setHouseBill: (houseBillId) => {
          const houseBill = get().masterBill?.houseBills?.find(
            (hbl) => hbl.id === houseBillId
          )
          if (houseBill) {
            set({
              houseBill: houseBill
            })
          }
        },

        // 根据ID获取 MBL 数据
        fetchMasterBill: async (masterBillId) => {
          set({ loading: true })
          try {
            const { success, message, data } =
              await customsV2Service.getMasterBill(masterBillId)
            if (success) {
              set({
                masterBill: data,
                houseBills: data?.houseBills || []
              })
              get().calculateTotalMarks()
            }
            return { success, message }
          } catch {
            return { success: false, message: '获取失败' }
          } finally {
            set({ loading: false })
          }
        },
        // 更新主提单数据
        updateMasterBill(data) {
          set({
            masterBill: {
              ...get().masterBill,
              ...data
            }
          })
        },

        updateFindHouseInMaster: async (data) => {
          const currentMasterBill = get().masterBill
          if (!currentMasterBill) return

          const updatedHouseBills = currentMasterBill.houseBills?.map((hb) =>
            hb.id === data.id ? data : hb
          ) || []

          set({
            masterBill: {
              ...currentMasterBill,
              houseBills: updatedHouseBills
            },
            houseBills: updatedHouseBills
          })

          // 如果更新的是当前选中的houseBill，也需要更新
          if (get().houseBill?.id === data.id) {
            set({ houseBill: data })
          }
        },

        // 更新备注
        updateNoteByHouseBill: async (note) => {
          const houseBillId = get().houseBill?.id
          if (!houseBillId) {
            return {
              success: false,
              message: '请选择子提单'
            }
          }
          set({ loading: true })
          try {
            const { success, message, data } =
              await customsV2Service.changeNote(houseBillId, note)
            if (success && data) {
              set({
                houseBills: get().houseBills.map((hb) =>
                  hb.id === data.id ? data : hb
                )
              })
            }

            return { success, message }
          } catch {
            return {
              success: false,
              message: '更新备注失败'
            }
          } finally {
            set({ loading: false })
          }
        },

        // 物理删除主提单
        removeMasterBill: async (masterBillId) => {
          set({ loading: true })
          try {
            const { success, message } =
              await customsV2Service.deleteMasterBill(masterBillId)
            return {
              success,
              message
            }
          } catch {
            return {
              success: false,
              message: '删除失败'
            }
          } finally {
            set({ loading: false })
          }
        },

        // 计算总件数（所有子提单的cargo_details数量之和）
        calculateTotalMarks: () => {
          const houseBills = get().houseBills || []
          const totalMarks = houseBills.reduce((total, houseBill) => {
            const cargoCount = houseBill.cargoDetails?.length || 0
            return total + cargoCount
          }, 0)
          set({ totalMarks: totalMarks })
        },


      }),
    
  )
)

// 导出状态选择器
export const useMasterBillState = () => {
  const masterBill = useMasterBillStore((state) => state.masterBill)
  const houseBills = useMasterBillStore((state) => state.houseBills)
  const houseBill = useMasterBillStore((state) => state.houseBill)
  const loading = useMasterBillStore((state) => state.loading)
  const error = useMasterBillStore((state) => state.error)
  const totalMarks = useMasterBillStore((state) => state.totalMarks)

  const printDialog = useMasterBillStore((state) => state.printDialog)
  const removeDialog = useMasterBillStore((state) => state.removeDialog)
  const noteDialog = useMasterBillStore((state) => state.noteDialog)
  return {
    masterBill,
    houseBill,
    houseBills,
    loading,
    error,
    totalMarks,

    printDialog,
    removeDialog,
    noteDialog
  }
}

// 导出动作选择器
export const useMasterBillActions = () => {

  const showPrintDialog = useMasterBillStore((state) => state.showPrintDialog)
  const closePrintDialog = useMasterBillStore((state) => state.closePrintDialog)

  const showNoteDialog = useMasterBillStore((state) => state.showNoteDialog)
  const closeNoteDialog = useMasterBillStore((state) => state.closeNoteDialog)

  const updateMasterBill = useMasterBillStore((state) => state.updateMasterBill)
  const updateNoteByHouseBill = useMasterBillStore(
    (state) => state.updateNoteByHouseBill
  )
  const removeMasterBill = useMasterBillStore((state) => state.removeMasterBill)
  const showRemoveDialog = useMasterBillStore((state) => state.showRemoveDialog)
  const closeRemoveDialog = useMasterBillStore(
    (state) => state.closeRemoveDialog
  )

  const fetchMasterBill = useMasterBillStore((state) => state.fetchMasterBill)
  const clearMasterBill = useMasterBillStore((state) => state.clearMasterBill)
  const setHouseBill = useMasterBillStore((state) => state.setHouseBill)
  const updateFindHouseInMaster = useMasterBillStore((state) => state.updateFindHouseInMaster)

  return {
    fetchMasterBill,
    updateMasterBill,
    clearMasterBill,
    removeMasterBill,

    updateNoteByHouseBill,
    setHouseBill,

    showPrintDialog,
    closePrintDialog,
    showRemoveDialog,
    closeRemoveDialog,
    showNoteDialog,
    closeNoteDialog,
    updateFindHouseInMaster
  }
}
